import { Field } from '@atlaskit/form';
import LinkIcon from '@atlaskit/icon/core/link';
import HyperlinkInlineIcon from '@atlaskit/icon/core/smart-link-inline';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Select from '@databases/atlaskit/Select';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './messages.ts';

const inputWrapperStyles = xcss({
  maxWidth: '100%',
  width: '266px'
});

const iconContainerStyles = xcss({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  justifyContent: 'center',
  width: '24px'
});

const HyperlinkDisplay = {
  INLINE: 'inline',
  URL: 'url'
} as const;

type HyperlinkDisplay = (typeof HyperlinkDisplay)[keyof typeof HyperlinkDisplay];

type HyperlinkDisplayOption = {
  label: string;
  value: HyperlinkDisplay;
};

type HyperlinkFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.HYPERLINK>;
};

const hyperlinkSelectStyles = getCompactSelectStyles<HyperlinkDisplayOption, false>();

const HyperlinkFieldSettings: FunctionComponent<HyperlinkFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const displayAsLabel = formatMessage(messages.hyperlinkDisplayAsLabel);
  const options = useMemo<HyperlinkDisplayOption[]>(
    () => [
      {
        label: formatMessage(messages.hyperlinkDisplayUrl),
        value: HyperlinkDisplay.URL
      },
      {
        label: formatMessage(messages.hyperlinkDisplayInline),
        value: HyperlinkDisplay.INLINE
      }
    ],
    [formatMessage]
  );
  const fieldSelectedValue =
    field.settings.resolve === false ? HyperlinkDisplay.URL : HyperlinkDisplay.INLINE;
  const [selectedValue, setSelectedValue] =
    useState<HyperlinkDisplay>(fieldSelectedValue);
  useEffect(() => {
    setSelectedValue(fieldSelectedValue);
  }, [fieldSelectedValue]);
  const selectedOption = options.find((option) => option.value === selectedValue);

  const handleDisplayChange = useCallback(
    (display: HyperlinkDisplay) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_RESOLVE,
          fieldId: field.id,
          resolve: display === HyperlinkDisplay.INLINE
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleSelectChange = useCallback(
    (option: HyperlinkDisplayOption | null) => {
      const nextValue = option?.value ?? selectedValue;
      setSelectedValue(nextValue);
      handleDisplayChange(nextValue);
    },
    [handleDisplayChange, selectedValue]
  );

  const formatOptionLabel = useCallback((option: HyperlinkDisplayOption) => {
    const icon =
      option.value === HyperlinkDisplay.URL ? (
        <LinkIcon label="" spacing="spacious" testId="hyperlink-url-icon" />
      ) : (
        <HyperlinkInlineIcon label="" spacing="spacious" testId="hyperlink-inline-icon" />
      );

    return (
      <Inline alignBlock="center" space="space.100">
        <Box as="span" xcss={iconContainerStyles}>
          {icon}
        </Box>
        {option.label}
      </Inline>
    );
  }, []);

  return (
    <Field
      label={
        <Text size="small" weight="bold" color="color.text.subtle">
          {displayAsLabel}
        </Text>
      }
      id="hyperlink-display-as"
      name="hyperlink-display-as-field"
      isRequired
    >
      {({ fieldProps }) => (
        <Box xcss={inputWrapperStyles}>
          <Select<HyperlinkDisplayOption>
            classNamePrefix="hyperlink-display-as"
            formatOptionLabel={formatOptionLabel}
            inputId={fieldProps.id}
            isClearable={false}
            isSearchable={false}
            onChange={handleSelectChange}
            options={options}
            spacing="compact"
            styles={hyperlinkSelectStyles}
            value={selectedOption}
          />
        </Box>
      )}
    </Field>
  );
};

export default HyperlinkFieldSettings;
